import { useCallback, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import {
  faCircleExclamation,
  faReceipt,
  faTruckArrowRight,
} from '@fortawesome/pro-solid-svg-icons'
import { observer } from 'mobx-react'

import { Alert } from 'Components/_theme/Alert/Alert'
import { CardActions } from 'Components/CardActions/CardActions'
import type { CardActionProps } from 'Components/CardActions/CardActions.types'
import { CardDetailsList } from 'Components/CardDetailsList/CardDetailsList'
import type { CardDetailsListItem } from 'Components/CardDetailsList/CardDetailsList.types'
import { ContentCard } from 'Components/ContentCard/ContentCard'
import { FormatDate } from 'Components/FormatDate/FormatDate'

import type { SenderCountrySelection } from 'Modals/CreateReturnLabelModal/CreateReturnLabel.types'
import { CreateReturnLabelModal } from 'Modals/CreateReturnLabelModal/CreateReturnLabelModal'
import { PortalReturnLabelModal } from 'Modals/PortalReturnLabelModal/PortalReturnLabelModal'
import type { PortalReturnLabelModalSubmitResult } from 'Modals/PortalReturnLabelModal/PortalReturnLabelModal.types'
import { UpdateTrackingModal } from 'Modals/UpdateTrackingModal/UpdateTrackingModal'

import { useActiveUser, useModal } from 'Hooks'

import { getCaseItemTypeTranslationId } from 'Utilities'

import {
  E2E_CREATE_RETURN_LABEL_ACTION,
  E2E_UPDATE_TRACKING_INFO_ACTION,
} from 'Constants/e2e'
import { supportedReturnCasePortalStatuses } from 'Constants/returnPortalStatus'

import { parseToSupportedPortalStatus } from 'Portal/src/utilities/getReturnCasePortalStatus/getReturnCasePortalStatus.utils'

import { CREATE_SHIPPING_LABEL_EXCLUDED_ORGS } from './ReturnDetailsCard.constants'
import {
  ReturnDetailsCardModalTypes,
  type ReturnDetailsCardProps,
} from './ReturnDetailsCard.types'

export const ReturnDetailsCard = observer((props: ReturnDetailsCardProps) => {
  const { returnCase, title, refetch } = props
  const intl = useIntl()

  const { activeOrganisation, activeEmployee } = useActiveUser()
  const organisation =
    activeOrganisation || activeEmployee?.organisation || null

  const { setModal, hideModal } = useModal()

  const handleUpdateTrackingSubmit = useCallback(async () => {
    const refetchResult = await refetch()

    hideModal()

    return refetchResult
  }, [hideModal, refetch])

  const handleCreateReturnLabelSubmit =
    useCallback(async (): Promise<PortalReturnLabelModalSubmitResult> => {
      if (!organisation?.id) {
        throw new Error(
          intl.formatMessage({
            id: 'PortalReturnLabelModal_return_label_refetch_error',
          }),
        )
      }

      const refetchResult = await refetch()
      const refetchedInfo = refetchResult.data.returnCaseItem?.returnLabelInfo

      return {
        dangerousGoodsPdf: refetchedInfo?.dangerousGoodsPdf ?? null,
        isDangerousGoods: refetchedInfo?.isDangerousGoods ?? null,
        organisationId: String(organisation.id),
        pdfDownloadUrl: refetchedInfo?.pdfDownloadUrl ?? null,
        trackingReference: refetchedInfo?.trackingReference ?? '',
      }
    }, [intl, organisation?.id, refetch])

  const generateDetailsCardListItems = useMemo(
    (): CardDetailsListItem[] => [
      ...(returnCase.returnDate
        ? [
            {
              label: 'ReturnDetails_return_date',
              value: <FormatDate value={returnCase.returnDate} />,
            },
          ]
        : []),
      {
        label: 'ReturnDetails_reason_for_return',
        value: returnCase.caseItem?.caseType ? (
          <FormattedMessage
            id={getCaseItemTypeTranslationId(returnCase.caseItem.caseType)}
          />
        ) : (
          '-'
        ),
      },
    ],
    [returnCase],
  )

  const handleOpenCreateReturnLabelModal = useCallback(() => {
    setModal({
      component: CreateReturnLabelModal,
      isOpen: true,
      props: {
        onClose: hideModal,
        onNewFlowMatch: (senderCountry: SenderCountrySelection) => {
          setModal({
            component: PortalReturnLabelModal,
            contentClassName: 'PortalReturnLabelModal-content',
            isOpen: true,
            props: {
              onClose: hideModal,
              onSubmit: handleCreateReturnLabelSubmit,
              returnCase,
              senderCountry,
            },
          })
        },
        returnCase,
      },
    })
  }, [hideModal, setModal, handleCreateReturnLabelSubmit, returnCase])

  const handleOpenUpdateTrackingModal = useCallback(() => {
    setModal({
      component: UpdateTrackingModal,
      isOpen: true,
      props: {
        handleSubmit: handleUpdateTrackingSubmit,
        returnCase,
      },
    })
  }, [setModal, handleUpdateTrackingSubmit, returnCase])

  const isReturnLabelEligibleOrg = useMemo(() => {
    if (
      !organisation?.id ||
      organisation?.currency === 'USD' ||
      returnCase.hideReturnLabel
    ) {
      return false
    }

    return !CREATE_SHIPPING_LABEL_EXCLUDED_ORGS.includes(
      Number(organisation.id),
    )
  }, [organisation, returnCase])

  // createReturnLabel requires a non-null employeeId and org-admin sessions have
  // no activeEmployee fallback, so hide the action when no employee resolves
  // rather than opening a modal whose submit can never satisfy the mutation.
  const hasResolvableEmployee = Boolean(
    returnCase.employee?.id ?? activeEmployee?.id,
  )
  const shouldShowCreateReturnLabelAction =
    isReturnLabelEligibleOrg && hasResolvableEmployee

  const shouldShowDeviceLockedAlert = useMemo(() => {
    const returnCasePortalStatusesForVisibleDeviceLockedAlert: string[] = [
      supportedReturnCasePortalStatuses['blocked-mdm-lock'],
      supportedReturnCasePortalStatuses['blocked-user-lock'],
      supportedReturnCasePortalStatuses['blocked-user-mdm-lock'],
    ]

    return returnCasePortalStatusesForVisibleDeviceLockedAlert.includes(
      parseToSupportedPortalStatus(returnCase.portalStatus),
    )
  }, [returnCase.portalStatus])

  const generateActions = useMemo(
    (): CardActionProps[] => [
      ...(shouldShowCreateReturnLabelAction
        ? [
            {
              e2eSelector: E2E_CREATE_RETURN_LABEL_ACTION,
              icon: faTruckArrowRight,
              id: ReturnDetailsCardModalTypes.CreateReturnLabelModal,
              name: 'Create_DHL_return_label',
              onActionClick: handleOpenCreateReturnLabelModal,
            },
          ]
        : []),
      {
        e2eSelector: E2E_UPDATE_TRACKING_INFO_ACTION,
        icon: faReceipt,
        id: ReturnDetailsCardModalTypes.UpdateTrackingModal,
        name: 'Update_tracking_information',
        onActionClick: handleOpenUpdateTrackingModal,
      },
    ],
    [
      handleOpenCreateReturnLabelModal,
      handleOpenUpdateTrackingModal,
      shouldShowCreateReturnLabelAction,
    ],
  )

  return (
    <ContentCard className="ReturnDetailsCard" title={title}>
      <div className="ReturnDetailsCard-body">
        <div>
          <CardDetailsList
            className="mb-4"
            listItems={generateDetailsCardListItems}
          />

          {shouldShowDeviceLockedAlert && (
            <Alert
              className="mt-4 mb-2"
              iconLeft={faCircleExclamation}
              variant="danger"
            >
              <FormattedMessage id="Device_locked_alert" />
            </Alert>
          )}
        </div>

        <CardActions actions={generateActions} type="return" />
      </div>
    </ContentCard>
  )
})
